/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {BaseNewFilter} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {calendarService, eventTypeService} from "services"
import {handleError} from "helpers"
import {get} from "lodash"
import {useQuery} from "@tanstack/react-query"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import styles from "./CalendarEventsTable.module.css"
import moment from "moment"
import {useGroupedOutcomes} from "hooks/useAllOutcomes"
import DepartmentSelect from "components/DepartmentSelect"
import {Checkbox, Col, Empty, Row, Spin} from "antd"
import {useAllDepartments, useDebouncedValue, useModel, useMyLinkedCalendars} from "hooks"
import {KlassDropdown} from "components/Select"
import {BaseRangePicker} from "components/DateTimePicker"
import CalendarEventItem from "./CalendarEventItem"

interface CalendarEvent {
    id?: number
    title?: string
    name?: string
    description?: string
    start_at_utc?: string
    end_at_utc?: string
    type?: string
    [key: string]: any
}

interface Props {
    studentId?: number
    departmentId?: number
    studentUserId?: number
}

type FilterState = {
    department: any
    myEventsOnly: boolean
    eventType: any
    search: string
    dateRange: [moment.Moment, moment.Moment] | null
}

const INITIAL_LAZY_WINDOW_DAYS = 30

function CalendarEventsTable(props: Props) {
    const {t} = useTranslation(["calendarEvents", "common"])
    const model = useModel()

    const groupedOutcomes = useGroupedOutcomes()
    const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingOlder, setIsLoadingOlder] = useState(false)
    const [isLoadingNewer, setIsLoadingNewer] = useState(false)
    const [loadedRange, setLoadedRange] = useState<{from: string; to: string} | null>(null)
    const [hasOlder, setHasOlder] = useState(false)
    const [hasNewer, setHasNewer] = useState(false)
    const loadOlderSentinelRef = useRef<HTMLDivElement | null>(null)
    const loadNewerSentinelRef = useRef<HTMLDivElement | null>(null)

    const {linkedCalendars} = useMyLinkedCalendars()
    const calendarIds = useMemo(
        () =>
            (linkedCalendars || [])
                .filter((calendar) => calendar.isPrimary || calendar.type === "group")
                .map((calendar) => calendar.calendarId)
                .filter(Boolean),
        [linkedCalendars]
    )

    const findOutcomeById = useCallback(
        (outcomeId?: number | null) => {
            if (!outcomeId) return null
            for (const group of groupedOutcomes) {
                const match = group.options.find((option) => option.outcomeId === outcomeId)
                if (match) return match
            }
            return null
        },
        [groupedOutcomes]
    )

    const handleUpdateOutcome = useCallback(async (record: CalendarEvent, outcomeId?: number) => {
        const objectId = get(record, "object_id")
        if (!objectId || !outcomeId) return
        try {
            await calendarService.updateObjectOutcome({object_id: +objectId, outcome_id: outcomeId})
        } catch (error) {
            handleError(error)
        }
    }, [])

    const {departments} = useAllDepartments()
    const initialDepartment = useMemo(
        () => (props.departmentId ? departments.find((d) => d.departmentId === props.departmentId) ?? null : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.departmentId, departments.length]
    )

    const defaultDateRange: [moment.Moment, moment.Moment] = useMemo(
        () => [moment().subtract(2, "months").startOf("day"), moment().endOf("day")],
        []
    )

    const [filter, setFilter] = useState<FilterState>({
        department: null,
        myEventsOnly: true,
        eventType: null,
        search: "",
        dateRange: defaultDateRange
    })
    const [appliedFilter, setAppliedFilter] = useState(filter)

    const debouncedSearch = useDebouncedValue(filter.search, 500)
    useEffect(() => {
        setAppliedFilter((prev) => ({...prev, search: debouncedSearch}))
    }, [debouncedSearch])

    useEffect(() => {
        if (initialDepartment) {
            setFilter((prev) => ({...prev, department: initialDepartment}))
            setAppliedFilter((prev) => ({...prev, department: initialDepartment}))
        }
    }, [initialDepartment])

    const {data: eventTypeOptions = []} = useQuery(
        ["calendar-event-types-all"],
        () =>
            eventTypeService.getAll({
                range: {limit: UNLIMITED_PAGE_SIZE, offset: 0},
                sort: {orderBy: "name", orderDir: "ASC"}
            }),
        {
            select: (result) =>
                (result.data || []).map((et: any) => ({
                    value: et.eventTypeId,
                    label: `${et.name} (${et.code})`
                }))
        }
    )

    const getDateBoundary = useCallback(
        (dateRange: [moment.Moment, moment.Moment] | null) => ({
            from: dateRange ? dateRange[0].clone().startOf("day") : moment().subtract(2, "months").startOf("day"),
            to: dateRange ? dateRange[1].clone().endOf("day") : moment().endOf("day")
        }),
        []
    )

    const normalizeEvents = useCallback((events: CalendarEvent[]) => {
        const uniqEvents = new Map<string, CalendarEvent>()
        events.forEach((event, index) => {
            const item = {
                ...event,
                id:
                    event.id ||
                    event.object_id ||
                    `${event.start_at_utc || "unknown-start"}-${event.end_at_utc || "unknown-end"}-${index}`,
                title: event.title || event.name || "-",
                eventType: get(event, "json_data.eventType", "-"),
                departmentName: get(event, "json_data.department.name", "-"),
                completedOutcomeId: get(event, "json_data.completedOutcomeId", null),
                type: event.type || "-",
                isChecked: false
            }
            const eventKey = `${item.id}`
            if (!uniqEvents.has(eventKey)) {
                uniqEvents.set(eventKey, item)
            }
        })
        return Array.from(uniqEvents.values()).sort(
            (first, second) => moment(second.start_at_utc).valueOf() - moment(first.start_at_utc).valueOf()
        )
    }, [])

    const getEventsByRange = useCallback(
        async (fromDate: moment.Moment, toDate: moment.Moment) => {
            if (!calendarIds.length) {
                return []
            }
            const response = await calendarService.getEvents({
                filter: {
                    from: fromDate.toISOString(),
                    to: toDate.toISOString(),
                    calendar_ids: calendarIds,
                    invited_user_id: props.studentUserId ? props.studentUserId : undefined,
                    owner_user_ids: [model.user?.id ?? 0],
                    include_booked: appliedFilter.myEventsOnly ? true : false,
                    include_invites: appliedFilter.myEventsOnly ? true : false,
                    include_course_schedules: true,
                    public_programs_end: true,
                    public_programs_start: true,
                    public_terms: true,
                    public_work_schedules: true
                }
            })
            const eventList: CalendarEvent[] = Array.isArray(response) ? response : get(response, "data", [])
            const searchLower = appliedFilter.search?.trim().toLowerCase()
            const filteredEvents = eventList.filter((event) => {
                if (appliedFilter.department?.departmentId) {
                    const departmentId = get(event, "json_data.departmentId")
                    if (departmentId !== appliedFilter.department.departmentId) return false
                }
                if (appliedFilter.eventType?.value) {
                    if (get(event, "json_data.eventTypeId") !== appliedFilter.eventType.value) return false
                }
                if (searchLower) {
                    const title = (event.title || event.name || "").toLowerCase()
                    const eventType = (get(event, "json_data.eventType") || "").toLowerCase()
                    const description = (event.description || "").toLowerCase()
                    if (
                        !title.includes(searchLower) &&
                        !eventType.includes(searchLower) &&
                        !description.includes(searchLower)
                    ) {
                        return false
                    }
                }
                return true
            })
            return normalizeEvents(filteredEvents)
        },
        [appliedFilter, model.user?.id, props.studentUserId, calendarIds, normalizeEvents]
    )

    useEffect(() => {
        ;(async function loadInitialWindow() {
            setIsLoading(true)
            try {
                const boundary = getDateBoundary(appliedFilter.dateRange)
                const currentDate = moment()
                const pivot = currentDate.isBefore(boundary.from)
                    ? boundary.from.clone()
                    : currentDate.isAfter(boundary.to)
                    ? boundary.to.clone()
                    : currentDate
                const halfWindowDays = INITIAL_LAZY_WINDOW_DAYS / 2
                const initialFrom = moment.max(
                    boundary.from.clone(),
                    pivot.clone().subtract(halfWindowDays, "days").startOf("day")
                )
                const initialTo = moment.min(
                    boundary.to.clone(),
                    pivot.clone().add(halfWindowDays, "days").endOf("day")
                )
                const events = await getEventsByRange(initialFrom, initialTo)
                setCalendarEvents(events)
                setLoadedRange({from: initialFrom.toISOString(), to: initialTo.toISOString()})
                setHasOlder(initialFrom.isAfter(boundary.from))
                setHasNewer(initialTo.isBefore(boundary.to))
            } catch (error) {
                handleError(error)
            } finally {
                setIsLoading(false)
            }
        })()
    }, [appliedFilter, getDateBoundary, getEventsByRange])

    const loadOlderEvents = useCallback(async () => {
        if (!loadedRange || !hasOlder || isLoadingOlder) return
        setIsLoadingOlder(true)
        try {
            const boundary = getDateBoundary(appliedFilter.dateRange)
            const currentFrom = moment(loadedRange.from)
            const nextTo = currentFrom.clone().subtract(1, "millisecond")
            const nextFrom = moment.max(
                boundary.from.clone(),
                nextTo.clone().subtract(INITIAL_LAZY_WINDOW_DAYS, "days").startOf("day")
            )
            if (nextFrom.isAfter(nextTo)) {
                setHasOlder(false)
                return
            }
            const olderEvents = await getEventsByRange(nextFrom, nextTo)
            setCalendarEvents((prev) => normalizeEvents([...prev, ...olderEvents]))
            setLoadedRange((prev) => (prev ? {from: nextFrom.toISOString(), to: prev.to} : prev))
            setHasOlder(nextFrom.isAfter(boundary.from))
        } catch (error) {
            handleError(error)
        } finally {
            setIsLoadingOlder(false)
        }
    }, [
        appliedFilter.dateRange,
        getDateBoundary,
        getEventsByRange,
        hasOlder,
        isLoadingOlder,
        loadedRange,
        normalizeEvents
    ])

    const loadNewerEvents = useCallback(async () => {
        if (!loadedRange || !hasNewer || isLoadingNewer) return
        setIsLoadingNewer(true)
        try {
            const boundary = getDateBoundary(appliedFilter.dateRange)
            const currentTo = moment(loadedRange.to)
            const nextFrom = currentTo.clone().add(1, "millisecond")
            const nextTo = moment.min(
                boundary.to.clone(),
                nextFrom.clone().add(INITIAL_LAZY_WINDOW_DAYS, "days").endOf("day")
            )
            if (nextFrom.isAfter(nextTo)) {
                setHasNewer(false)
                return
            }
            const newerEvents = await getEventsByRange(nextFrom, nextTo)
            setCalendarEvents((prev) => normalizeEvents([...newerEvents, ...prev]))
            setLoadedRange((prev) => (prev ? {from: prev.from, to: nextTo.toISOString()} : prev))
            setHasNewer(nextTo.isBefore(boundary.to))
        } catch (error) {
            handleError(error)
        } finally {
            setIsLoadingNewer(false)
        }
    }, [
        appliedFilter.dateRange,
        getDateBoundary,
        getEventsByRange,
        hasNewer,
        isLoadingNewer,
        loadedRange,
        normalizeEvents
    ])

    useEffect(() => {
        if (!hasOlder || !loadOlderSentinelRef.current) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    loadOlderEvents()
                }
            },
            {root: null, rootMargin: "150px 0px"}
        )

        observer.observe(loadOlderSentinelRef.current)
        return () => observer.disconnect()
    }, [hasOlder, loadOlderEvents])

    useEffect(() => {
        if (!hasNewer || !loadNewerSentinelRef.current) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    loadNewerEvents()
                }
            },
            {root: null, rootMargin: "150px 0px"}
        )

        observer.observe(loadNewerSentinelRef.current)
        return () => observer.disconnect()
    }, [hasNewer, loadNewerEvents])

    return (
        <div className={styles.container}>
            <BaseNewFilter
                filter={filter}
                searchValue={filter.search}
                showSearch={true}
                onSearchInput={(value) => setFilter((prev) => ({...prev, search: value}))}
                onClick={() => setAppliedFilter(filter)}
                onClickClear={() => {
                    const defaultFilter = {
                        department: null,
                        myEventsOnly: false,
                        eventType: null,
                        search: "",
                        dateRange: null
                    }
                    setFilter(defaultFilter)
                    setAppliedFilter(defaultFilter)
                }}>
                <Row gutter={[24, 24]}>
                    <Col span={12}>
                        <DepartmentSelect
                            isClearable
                            value={filter.department}
                            onChange={(newValue) => setFilter((prev) => ({...prev, department: newValue}))}
                            placeholder={t("calendarEvents.department")}
                        />
                    </Col>
                    <Col span={12}>
                        <KlassDropdown
                            value={filter.eventType}
                            options={eventTypeOptions}
                            valueKey="value"
                            labelKey="label"
                            isClearable
                            onChange={(newValue) => setFilter((prev) => ({...prev, eventType: newValue}))}
                            placeholder={t("calendarEvents.eventType")}
                        />
                    </Col>
                    <Col span={12}>
                        <BaseRangePicker
                            value={filter.dateRange}
                            onChange={(newValue) =>
                                setFilter((prev) => ({
                                    ...prev,
                                    dateRange: newValue as [moment.Moment, moment.Moment] | null
                                }))
                            }
                            placeholder={[t("calendarEvents.startDate"), t("calendarEvents.endDate")]}
                            allowClear
                        />
                    </Col>
                    <Col span={12}>
                        <Checkbox
                            checked={filter.myEventsOnly}
                            onChange={(event) => setFilter((prev) => ({...prev, myEventsOnly: event.target.checked}))}>
                            {t("calendarEvents.myEventsOnly")}
                        </Checkbox>
                    </Col>
                </Row>
            </BaseNewFilter>
            <div className={styles.cardListWrapper}>
                {isLoading && (
                    <div className={styles.loadingOverlay}>
                        <Spin size="large" />
                    </div>
                )}
                <div className={styles.cardList}>
                    {hasNewer && (
                        <div ref={loadNewerSentinelRef} className={styles.lazyLoadSentinel}>
                            {isLoadingNewer && <Spin size="small" />}
                        </div>
                    )}
                    {calendarEvents?.length ? (
                        calendarEvents.map((event: CalendarEvent) => (
                            <CalendarEventItem
                                key={event.id}
                                event={event}
                                outcomeValue={findOutcomeById(get(event, "json_data.completedOutcomeId"))}
                                handleUpdateOutcome={handleUpdateOutcome}
                            />
                        ))
                    ) : (
                        <Empty description={t("common:table.emptyData")} />
                    )}
                    {hasOlder && (
                        <div ref={loadOlderSentinelRef} className={styles.lazyLoadSentinel}>
                            {isLoadingOlder && <Spin size="small" />}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default KlassappTableHOC(CalendarEventsTable)
